import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Loader2, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { siteNetworkService } from '../../../../../features/billValidation/services/siteNetworkService';
import { ntaRuleService } from '../../../../../features/billValidation/services/ntaRuleService';
import { NewBadge } from '../../../../../features/billValidation/components/NewBadge';
import {
  NTA_CLASSIFICATIONS, NTA_CUSTOMER_CLASSES, NTA_TARIFF_TYPES, NTA_VOLTAGES, describeRule, parseRulesCsv, rulesToCsv, tariffCoverage,
  validateRule, type NtaRule, type ParsedRules, type TariffCoverage,
} from '../../../../../features/billValidation/lib/ntaRules';

// Admin → NTA Thresholds (spec 5, BR-12). One row per network tariff known to
// the platform (bv_site_network), with the eligibility rule in force on the
// chosen date. A tariff with no rule is never proposed as an alternative.
// Open-to-new (T-08) is shown here but edited on the Network Tariffs tab.

const today = () => new Date().toISOString().slice(0, 10);

type Form = {
  id?: string;
  distributor: string; tariff_code: string; effective_from: string; effective_to: string;
  min_usage_kwh: string; max_usage_kwh: string; min_demand: string; max_demand: string; demand_unit: '' | 'kVA' | 'kW';
  classifications: string[]; connection_voltages: string[]; tariff_type: string; export_rule: '' | 'required' | 'not_allowed';
  source_document: string; class_min: string; class_max: string; tariff_role: '' | 'primary' | 'secondary'; location: string;
  // Sheet-only fields, carried through unedited.
  sheet: Pick<NtaRule, 'usage_profile' | 'demand_profile' | 'source_text' | 'review_flags'>;
};

const s = (v: number | null) => (v == null ? '' : String(v));
const toForm = (r: Partial<NtaRule>): Form => ({
  id: r.id, distributor: r.distributor ?? '', tariff_code: r.tariff_code ?? '',
  effective_from: r.effective_from ?? today(), effective_to: r.effective_to ?? '',
  min_usage_kwh: s(r.min_usage_kwh ?? null), max_usage_kwh: s(r.max_usage_kwh ?? null),
  min_demand: s(r.min_demand ?? null), max_demand: s(r.max_demand ?? null), demand_unit: r.demand_unit ?? '',
  classifications: r.classifications ?? [], connection_voltages: r.connection_voltages ?? [],
  tariff_type: r.tariff_type ?? '', export_rule: r.export_rule ?? '', source_document: r.source_document ?? '',
  class_min: r.class_min ?? '', class_max: r.class_max ?? '', tariff_role: r.tariff_role ?? '', location: r.location ?? '',
  sheet: { usage_profile: r.usage_profile ?? null, demand_profile: r.demand_profile ?? null,
    source_text: r.source_text ?? null, review_flags: r.review_flags ?? null },
});
const n = (v: string) => (v.trim() === '' ? null : Number(v.replace(/,/g, '')));
const fromForm = (f: Form): NtaRule => ({
  id: f.id, distributor: f.distributor.trim(), tariff_code: f.tariff_code.trim().toUpperCase(),
  effective_from: f.effective_from, effective_to: f.effective_to || null,
  min_usage_kwh: n(f.min_usage_kwh), max_usage_kwh: n(f.max_usage_kwh),
  min_demand: n(f.min_demand), max_demand: n(f.max_demand), demand_unit: f.demand_unit || null,
  classifications: f.classifications.length ? f.classifications : null,
  connection_voltages: f.connection_voltages.length ? f.connection_voltages : null,
  tariff_type: f.tariff_type || null, export_rule: f.export_rule || null, source_document: f.source_document.trim() || null,
  class_min: f.class_min || null, class_max: f.class_max || null, tariff_role: f.tariff_role || null,
  location: f.location.trim().toUpperCase() || null, ...f.sheet,
});

const sameKey = (a: NtaRule, b: NtaRule) =>
  a.distributor === b.distributor && a.tariff_code === b.tariff_code && a.effective_from === b.effective_from;

const download = (text: string, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const Chips = ({ options, value, onChange }: { options: string[]; value: string[]; onChange: (v: string[]) => void }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button key={o} type="button" aria-pressed={on}
          onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`rounded-full border px-2.5 py-0.5 text-xs ${on ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}>
          {o}
        </button>
      );
    })}
  </div>
);

export const NtaThresholdsManagement: React.FC = () => {
  const [networkRows, setNetworkRows] = useState<Parameters<typeof tariffCoverage>[0]>([]);
  const [rules, setRules] = useState<NtaRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onDate, setOnDate] = useState(today());
  const [distributor, setDistributor] = useState('');
  const [search, setSearch] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [parsed, setParsed] = useState<ParsedRules | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [net, r] = await Promise.all([siteNetworkService.listAll(), ntaRuleService.list()]);
    if (!net.success) setError(net.error || 'Failed to load network tariffs');
    else if (!r.success) setError(r.error || 'Failed to load thresholds');
    setNetworkRows((net.data ?? []).map((x) => ({
      distributor: x.distributor ?? null, tariff_code: x.tariff_code, tariff_name: x.tariff_name ?? null,
      open_to_new_applicants: x.open_to_new_applicants ?? null,
    })));
    setRules(r.data ?? []);
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const coverage = useMemo(() => tariffCoverage(networkRows, rules, onDate), [networkRows, rules, onDate]);
  const distributors = useMemo(() => [...new Set(coverage.map((c) => c.distributor))], [coverage]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coverage.filter((c) => (!distributor || c.distributor === distributor)
      && (!missingOnly || !c.rule)
      && (!q || c.tariffCode.toLowerCase().includes(q) || (c.tariffName ?? '').toLowerCase().includes(q)));
  }, [coverage, distributor, search, missingOnly]);
  const withRule = coverage.filter((c) => c.rule).length;
  const historyOf = (c: TariffCoverage) => rules.filter((r) => r.distributor === c.distributor && r.tariff_code === c.tariffCode);

  const formErrors = useMemo(() => (form ? validateRule(fromForm(form), rules) : []), [form, rules]);

  const save = async () => {
    if (!form || formErrors.length) return;
    setSaving(true);
    setSaveError(null);
    const res = await ntaRuleService.save(fromForm(form));
    setSaving(false);
    if (!res.success) { setSaveError(res.error || 'Save failed'); return; }
    setForm(null);
    await load();
  };

  const remove = async (r: NtaRule) => {
    if (!r.id || !window.confirm(`Delete the ${r.tariff_code} rule effective ${r.effective_from}?`)) return;
    const res = await ntaRuleService.remove(r.id);
    if (!res.success) { setError(res.error || 'Delete failed'); return; }
    await load();
  };

  const pickFile = async (file: File) => {
    const p = parseRulesCsv(await file.text());
    // Rows replacing an existing (distributor, code, from) are updates; anything
    // else must not overlap a stored period.
    const errors = [...p.errors];
    const ok: NtaRule[] = [];
    p.rules.forEach((r) => {
      const others = rules.filter((x) => !sameKey(x, r));
      const e = validateRule(r, others);
      if (e.length) errors.push({ line: 0, error: `${r.distributor} ${r.tariff_code} ${r.effective_from}: ${e.join('; ')}` });
      else ok.push(r);
    });
    setParsed({ rules: ok, errors });
    setImportMsg(null);
  };

  const runImport = async () => {
    if (!parsed?.rules.length) return;
    setImporting(true);
    const res = await ntaRuleService.bulkUpsert(parsed.rules);
    setImporting(false);
    if (!res.success) { setImportMsg(res.error || 'Import failed'); return; }
    setImportOpen(false);
    setParsed(null);
    await load();
  };

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold">NTA Eligibility Thresholds <NewBadge /></h2>
          <p className="text-sm text-muted-foreground">
            The thresholds a site must meet for a network tariff to be proposed. All thresholds must pass; a blank
            threshold is no constraint. A tariff with no rule in force is never proposed as an alternative.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setImportOpen(true); setParsed(null); setImportMsg(null); }}>
            <Upload className="mr-1 h-4 w-4" /> Import CSV
          </Button>
          <Button variant="outline" size="sm" disabled={!rules.length}
            onClick={() => download(rulesToCsv(rules), `nta_thresholds_${today()}.csv`)}>
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
          <Button size="sm" onClick={() => { setSaveError(null); setForm(toForm({})); }}>
            <Plus className="mr-1 h-4 w-4" /> Add rule
          </Button>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Network tariffs', coverage.length],
          ['With a rule in force', withRule],
          ['Without a rule', coverage.length - withRule],
          ['Closed to new customers', coverage.filter((c) => !c.openToNew).length],
        ].map(([label, value]) => (
          <Card key={label}><CardContent className="px-4 py-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold">{loading ? '…' : value}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="nta-on-date" className="text-xs">In force on</Label>
          <Input id="nta-on-date" type="date" className="w-40" value={onDate} onChange={(e) => setOnDate(e.target.value || today())} />
        </div>
        <div>
          <Label htmlFor="nta-filter-distributor" className="text-xs">Distributor</Label>
          <select id="nta-filter-distributor" className="block h-9 rounded-md border bg-background px-2 text-sm" value={distributor}
            onChange={(e) => setDistributor(e.target.value)}>
            <option value="">All distributors</option>
            {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="relative">
          <Label htmlFor="nta-filter-search" className="text-xs">Search</Label>
          <Search className="absolute left-2 top-[30px] h-4 w-4 text-muted-foreground" />
          <Input id="nta-filter-search" className="w-56 pl-8" placeholder="Tariff code or name" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />
          Without a rule only
        </label>
      </div>

      <Card>
        <Table data-testid="nta-thresholds-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Distributor</TableHead>
              <TableHead>Tariff</TableHead>
              <TableHead>Open to new</TableHead>
              <TableHead>Thresholds in force</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…
              </TableCell></TableRow>
            )}
            {!loading && shown.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">No tariffs match.</TableCell></TableRow>
            )}
            {!loading && shown.map((c) => {
              const key = `${c.distributor}|${c.tariffCode}`;
              const history = historyOf(c);
              const isOpen = open.has(key);
              return (
                <React.Fragment key={key}>
                  <TableRow data-testid="nta-threshold-row">
                    <TableCell>
                      {history.length > 0 && (
                        <button type="button" aria-expanded={isOpen} aria-label="Show rule history"
                          onClick={() => setOpen((p) => { const x = new Set(p); if (x.has(key)) x.delete(key); else x.add(key); return x; })}>
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{c.distributor}</TableCell>
                    <TableCell>
                      <div className="font-mono text-sm">{c.tariffCode}</div>
                      {c.tariffName && <div className="text-xs text-muted-foreground">{c.tariffName}</div>}
                    </TableCell>
                    <TableCell title="Edited on the Network Tariffs tab">
                      {c.openToNew ? <Badge variant="outline">Yes</Badge> : <Badge variant="secondary">Closed</Badge>}
                    </TableCell>
                    <TableCell>
                      {c.rule ? (
                        <div className="flex flex-wrap gap-1">
                          {describeRule(c.rule).map((d) => <Badge key={d} variant="outline" className="font-normal">{d}</Badge>)}
                          {describeRule(c.rule).length === 0 && <span className="text-xs text-muted-foreground">No constraints</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-amber-700">No rule — not proposed as an alternative</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {c.rule ? `${c.rule.effective_from} – ${c.rule.effective_to ?? 'open'}` : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.rule ? (
                        <Button variant="ghost" size="sm" onClick={() => { setSaveError(null); setForm(toForm(c.rule!)); }}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm"
                          onClick={() => { setSaveError(null); setForm(toForm({ distributor: c.distributor, tariff_code: c.tariffCode })); }}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Add
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && history.map((r) => (
                    <TableRow key={r.id} className="bg-muted/40">
                      <TableCell />
                      <TableCell colSpan={3} className="text-xs text-muted-foreground">
                        {r.effective_from} – {r.effective_to ?? 'open'}{r.source_document ? ` · ${r.source_document}` : ''}
                      </TableCell>
                      <TableCell className="text-xs">{describeRule(r).join(' · ') || 'No constraints'}</TableCell>
                      <TableCell />
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" aria-label="Edit rule" onClick={() => { setSaveError(null); setForm(toForm(r)); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" aria-label="Delete rule" onClick={() => void remove(r)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* ── Add / edit rule ─────────────────────────────────────────────── */}
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Edit' : 'Add'} eligibility rule</DialogTitle>
            <DialogDescription>Blank thresholds are not tested. Bounds are inclusive.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nta-distributor">Distributor</Label>
                <select id="nta-distributor" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.distributor}
                  disabled={!!form.id} onChange={(e) => set('distributor', e.target.value)}>
                  <option value="">Select…</option>
                  {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="nta-code">Tariff code</Label>
                <select id="nta-code" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.tariff_code}
                  disabled={!!form.id || !form.distributor} onChange={(e) => set('tariff_code', e.target.value)}>
                  <option value="">Select…</option>
                  {coverage.filter((c) => c.distributor === form.distributor).map((c) => (
                    <option key={c.tariffCode} value={c.tariffCode}>{c.tariffCode}{c.tariffName ? ` — ${c.tariffName}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="nta-from">Effective from</Label>
                <Input id="nta-from" type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="nta-to">Effective to <span className="text-muted-foreground">(blank = open)</span></Label>
                <Input id="nta-to" type="date" value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="nta-min-usage">Min annual usage (kWh) <span className="text-muted-foreground">T-05</span></Label>
                <Input id="nta-min-usage" inputMode="decimal" value={form.min_usage_kwh} onChange={(e) => set('min_usage_kwh', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="nta-max-usage">Max annual usage (kWh) <span className="text-muted-foreground">T-06</span></Label>
                <Input id="nta-max-usage" inputMode="decimal" value={form.max_usage_kwh} onChange={(e) => set('max_usage_kwh', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="nta-min-demand">Min demand <span className="text-muted-foreground">T-03</span></Label>
                <Input id="nta-min-demand" inputMode="decimal" value={form.min_demand} onChange={(e) => set('min_demand', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="nta-max-demand">Max demand <span className="text-muted-foreground">T-04</span></Label>
                <div className="flex gap-2">
                  <Input id="nta-max-demand" inputMode="decimal" value={form.max_demand} onChange={(e) => set('max_demand', e.target.value)} />
                  <select aria-label="Demand unit" className="h-9 rounded-md border bg-background px-2 text-sm" value={form.demand_unit}
                    onChange={(e) => set('demand_unit', e.target.value as Form['demand_unit'])}>
                    <option value="">Unit</option>
                    <option value="kVA">kVA</option>
                    <option value="kW">kW</option>
                  </select>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Classification <span className="text-muted-foreground">T-01 · MSATS · none selected = any</span></Label>
                <div className="mt-1"><Chips options={NTA_CLASSIFICATIONS} value={form.classifications} onChange={(v) => set('classifications', v)} /></div>
              </div>
              <div className="col-span-2">
                <Label>Connection voltage <span className="text-muted-foreground">T-02 · none selected = any</span></Label>
                <div className="mt-1"><Chips options={NTA_VOLTAGES} value={form.connection_voltages} onChange={(v) => set('connection_voltages', v)} /></div>
              </div>
              <div>
                <Label htmlFor="nta-type">Tariff type <span className="text-muted-foreground">T-07</span></Label>
                <select id="nta-type" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.tariff_type}
                  onChange={(e) => set('tariff_type', e.target.value)}>
                  <option value="">—</option>
                  {NTA_TARIFF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="nta-export">Export (solar) <NewBadge /></Label>
                <select id="nta-export" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.export_rule}
                  onChange={(e) => set('export_rule', e.target.value as Form['export_rule'])}>
                  <option value="">No rule</option>
                  <option value="required">Required</option>
                  <option value="not_allowed">Not allowed</option>
                </select>
              </div>
              <div>
                <Label htmlFor="nta-class-min">Customer class from <NewBadge /></Label>
                <select id="nta-class-min" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.class_min} onChange={(e) => set('class_min', e.target.value)}>
                  <option value="">Any</option>
                  {NTA_CUSTOMER_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="nta-class-max">Customer class to <NewBadge /></Label>
                <select id="nta-class-max" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.class_max} onChange={(e) => set('class_max', e.target.value)}>
                  <option value="">Same as from</option>
                  {NTA_CUSTOMER_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="nta-role">Tariff role <NewBadge /></Label>
                <select id="nta-role" className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.tariff_role}
                  onChange={(e) => set('tariff_role', e.target.value as Form['tariff_role'])}>
                  <option value="">—</option>
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary (never an alternative)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="nta-location">Location only <NewBadge /></Label>
                <Input id="nta-location" className="mt-1" placeholder="e.g. CBD" value={form.location} onChange={(e) => set('location', e.target.value)} />
              </div>
              {(form.sheet.source_text || form.sheet.review_flags?.length) && (
                <div className="col-span-2 rounded-md border bg-muted/40 p-2 text-xs" data-testid="nta-sheet-info">
                  {form.sheet.source_text && <p><span className="font-medium">Sheet wording:</span> {form.sheet.source_text}</p>}
                  {(form.sheet.usage_profile || form.sheet.demand_profile) && (
                    <p><span className="font-medium">Profiles:</span> usage {form.sheet.usage_profile ?? '—'}, demand {form.sheet.demand_profile ?? '—'}</p>
                  )}
                  {form.sheet.review_flags?.length ? (
                    <ul className="mt-1 list-disc pl-4 text-amber-700">{form.sheet.review_flags.map((f) => <li key={f}>{f}</li>)}</ul>
                  ) : null}
                </div>
              )}
              <div className="col-span-2">
                <Label htmlFor="nta-source">Source document</Label>
                <Input id="nta-source" placeholder="e.g. SAPN Tariff Structure Statement 2025-26, p.14" value={form.source_document}
                  onChange={(e) => set('source_document', e.target.value)} />
              </div>
            </div>
          )}
          {formErrors.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-red-600" data-testid="nta-rule-errors">
              {formErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {saveError && <Alert variant="destructive"><AlertDescription>{saveError}</AlertDescription></Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving || formErrors.length > 0}>
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CSV import ─────────────────────────────────────────────────── */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import thresholds from CSV</DialogTitle>
            <DialogDescription>
              Columns: distributor, tariff_code, effective_from (required), effective_to, classifications,
              connection_voltages (|-separated), min_demand, max_demand, demand_unit, min_usage_kwh, max_usage_kwh,
              tariff_type, export_rule (required / not_allowed), source_document, class_min, class_max, tariff_role
              (primary / secondary), location, usage_profile, demand_profile, source_text. A row with the same distributor, tariff and effective
              from as a stored rule replaces it. Distributor and tariff code must match Network Tariffs exactly.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="text-sm"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickFile(f); }} />
            <Button variant="ghost" size="sm" onClick={() => download(rulesToCsv([]), 'nta_thresholds_template.csv')}>
              <Download className="mr-1 h-4 w-4" /> Template
            </Button>
          </div>
          {parsed && (
            <div className="space-y-2 text-sm">
              <p><strong>{parsed.rules.length}</strong> row(s) ready to import
                {parsed.rules.length > 0 && (() => {
                  const unknown = parsed.rules.filter((r) => !coverage.some((c) => c.distributor === r.distributor && c.tariffCode === r.tariff_code));
                  return unknown.length ? <span className="text-amber-700"> · {unknown.length} not in Network Tariffs ({unknown.slice(0, 3).map((u) => u.tariff_code).join(', ')}{unknown.length > 3 ? '…' : ''})</span> : null;
                })()}
              </p>
              {parsed.errors.length > 0 && (
                <div className="max-h-48 overflow-auto rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {parsed.errors.map((e, i) => <div key={i}>{e.line ? `Line ${e.line}: ` : ''}{e.error}</div>)}
                </div>
              )}
            </div>
          )}
          {importMsg && <Alert variant="destructive"><AlertDescription>{importMsg}</AlertDescription></Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={() => void runImport()} disabled={importing || !parsed?.rules.length}>
              {importing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Import {parsed?.rules.length ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
